import React, { useState, useMemo } from 'react';
import { getMarketAdjustedRates, MICHIGAN_MARKETS } from '../data/michiganMarketData';

// Define how many slots each unit type consumes
const UNIT_SLOT_REQUIREMENTS = {
  studio: 1,
  oneBR: 2,
  twoBR: 3,
  studioPlus: 1, // Part of 3-slot pair with 1BR+
  oneBRPlus: 2   // Part of 3-slot pair with Studio+
};

// Egress calculation logic - MUST match main component (SimpleModIQVisualizer.jsx)
function calculateEgressSlots(totalBoxes) {
  const egressSlots = [];
  const maxTravelDistance = 3; // No unit can be more than 3 slots away from egress
  
  if (totalBoxes <= 4) {
    // Very small buildings: one egress in the middle
    const middleSlot = Math.floor(totalBoxes / 2);
    if (middleSlot > 0 && middleSlot < totalBoxes - 1) {
      egressSlots.push(middleSlot);
    }
  } else {
    // Larger buildings: place egresses to ensure 3-slot coverage
    // Each egress covers 7 slots total (3 left + egress + 3 right)
    // Place egresses every 6 slots to ensure overlap and complete coverage
    
    let position = 2; // Start at position 2 (3rd box)
    
    while (position < totalBoxes - 1) {
      egressSlots.push(position);
      // Next egress position: move by 6 to ensure coverage overlap
      position += 6;
    }
    
    // Check if the last boxes need additional coverage
    const lastEgressPosition = egressSlots[egressSlots.length - 1];
    const distanceToEnd = totalBoxes - 1 - lastEgressPosition;
    
    if (distanceToEnd > maxTravelDistance) {
      // Add egress near the end to cover remaining boxes
      const additionalEgress = Math.max(lastEgressPosition + 3, totalBoxes - 4);
      if (additionalEgress < totalBoxes - 1 && additionalEgress > lastEgressPosition) {
        egressSlots.push(additionalEgress);
      }
    }
  }

  return egressSlots;
}

const IntelligentUnitMixAdvisor = ({ 
  buildingBoxes = [], 
  floorCount = 1, 
  unitQuantities = {}, 
  selectedMarket = 'GRAND_RAPIDS', // Now passed from parent
  customRentOverrides = null, // Custom rent rates from parent
  onApplyRecommendation 
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const [showAIReasoning, setShowAIReasoning] = useState(false); // Collapsed by default
  const [showMarketAnalysis, setShowMarketAnalysis] = useState(false); // Collapsed by default
  const [showMaxRevenue, setShowMaxRevenue] = useState(true); // Collapsible Max Revenue panel
  
  // Calculate available slots from building boxes (accounting for egress)
  const { availableSlots, totalSlots, egressSlots, egressSlotsTotal } = useMemo(() => {
    // Ensure buildingBoxes is an array
    const safeBuildingBoxes = Array.isArray(buildingBoxes) ? buildingBoxes : [];
    if (safeBuildingBoxes.length === 0) {
      return { availableSlots: 0, totalSlots: 0, egressSlots: [], egressSlotsTotal: 0 };
    }
    
    const totalBoxes = safeBuildingBoxes.length;
    const egressSlots = calculateEgressSlots(totalBoxes);
    
    // Calculate total slots (both sides of each box, multiplied by floors)
    const totalSlots = totalBoxes * 2 * floorCount;
    
    // Egress slots take up only LEFT side of egress towers (1 slot per egress)
    const egressSlotsTotal = egressSlots.length * floorCount;
    
    // Available slots for units = total slots minus egress slots
    const availableSlots = totalSlots - egressSlotsTotal;
    
    return { availableSlots, totalSlots, egressSlots, egressSlotsTotal };
  }, [buildingBoxes, floorCount]);

  // Get current market data
  const currentMarket = MICHIGAN_MARKETS[selectedMarket];

  // Calculate total slots consumed by a unit mix
  const calculateSlotsUsed = (unitMix) => {
    let totalSlots = 0;
    
    // Handle standalone units
    totalSlots += (unitMix.studio || 0) * 1;
    totalSlots += (unitMix.oneBR || 0) * 2;
    totalSlots += (unitMix.twoBR || 0) * 3;
    
    // Handle paired units (Studio+ & 1BR+ together = 3 slots per pair)
    const studioPlus = unitMix.studioPlus || 0;
    const oneBRPlus = unitMix.oneBRPlus || 0;
    const pairs = Math.max(studioPlus, oneBRPlus); // They should always be equal, but take max just in case
    totalSlots += pairs * 3;
    
    return totalSlots;
  };

  // Calculate revenue for a unit mix using real Michigan data or custom rates
  const calculateRevenue = React.useCallback((units, businessModelMix) => {
    let totalRevenue = 0;
    
    Object.keys(units).forEach(unitType => {
      const unitCount = units[unitType] || 0;
      if (unitCount === 0) return;
      
      let weightedRent = 0;
      
      // Use custom rent overrides if available
      if (customRentOverrides && customRentOverrides[unitType]) {
        weightedRent = customRentOverrides[unitType];
      } else {
        // Calculate weighted average rent based on business model distribution
        Object.keys(businessModelMix).forEach(businessModel => {
          const percentage = businessModelMix[businessModel] || 0;
          const marketData = getMarketAdjustedRates(selectedMarket, businessModel);
          if (marketData && marketData.rates[unitType]) {
            weightedRent += (marketData.rates[unitType] * percentage / 100);
          }
        });
      }
      
      totalRevenue += unitCount * weightedRent;
    });
    
    return Math.round(totalRevenue);
  }, [selectedMarket, customRentOverrides]);

  // Generate unit mix that fits within available slots
  const generateOptimalUnitMix = React.useCallback((strategy, availableSlots) => {
    let unitMix = { studio: 0, oneBR: 0, twoBR: 0, studioPlus: 0, oneBRPlus: 0 };
    let remainingSlots = availableSlots;

    if (strategy === 'revenue') {
      // Revenue strategy: Actually maximize revenue using greedy algorithm
      
      // Calculate revenue per slot for each unit type
      const defaultBusinessMix = { marketRate: 60, shortTermRental: 30, workforceHousing: 10 };
      
      // Define unit types - treat Studio+ & 1BR+ as a PAIRED unit
      const unitTypes = [
        { key: 'twoBR', slots: 3, type: 'standalone' },
        { key: 'oneBR', slots: 2, type: 'standalone' },
        { key: 'studioPlusPair', slots: 3, type: 'paired' }, // Studio+ & 1BR+ together
        { key: 'studio', slots: 1, type: 'standalone' }
      ];
      
      // Calculate revenue per slot for each unit type
      const revenuePerSlot = unitTypes.map(unit => {
        let testMix = {};
        let revenue = 0;
        
        if (unit.type === 'paired') {
          // For the paired unit, calculate combined revenue of Studio+ and 1BR+
          testMix = { studioPlus: 1, oneBRPlus: 1 };
          revenue = calculateRevenue(testMix, defaultBusinessMix);
        } else {
          // For standalone units
          testMix = { [unit.key]: 1 };
          revenue = calculateRevenue(testMix, defaultBusinessMix);
        }
        
        const revenuePerSlot = revenue / unit.slots;
        
        return {
          ...unit,
          revenue,
          revenuePerSlot
        };
      }).sort((a, b) => b.revenuePerSlot - a.revenuePerSlot); // Sort by revenue per slot, highest first
      
      console.log('🎯 Revenue per slot analysis (corrected for tetris units):', revenuePerSlot.map(u => 
        `${u.key}: $${u.revenue}/month per unit, $${u.revenuePerSlot.toFixed(0)}/slot`
      ));
      
      // Greedy allocation: prioritize highest revenue per slot
      for (const unitType of revenuePerSlot) {
        // Calculate how many of this unit type we can fit
        const maxUnits = Math.floor(remainingSlots / unitType.slots);
        
        if (maxUnits > 0) {
          let unitsToAllocate;
          
          if (unitType.key === 'twoBR') {
            // Be extremely aggressive with 2BR - use up to 80% of remaining slots for max revenue
            unitsToAllocate = Math.min(maxUnits, Math.floor(remainingSlots * 0.8 / unitType.slots));
            // Ensure we place at least 1 if possible
            unitsToAllocate = Math.max(unitsToAllocate, Math.min(1, maxUnits));
          } else if (unitType.key === 'oneBR') {
            // Be very aggressive with 1BR - use up to 90% of remaining slots  
            unitsToAllocate = Math.min(maxUnits, Math.floor(remainingSlots * 0.9 / unitType.slots));
            // Ensure we place at least 1 if possible
            unitsToAllocate = Math.max(unitsToAllocate, Math.min(1, maxUnits));
          } else if (unitType.key === 'studioPlusPair') {
            // Use Studio+/1BR+ pairs aggressively - they generate more revenue than studios
            unitsToAllocate = Math.min(maxUnits, Math.floor(remainingSlots * 0.7 / unitType.slots));
          } else {
            // For studios, AVOID COMPLETELY unless no other units can fit
            const canFit3Slot = remainingSlots >= 3; // Can fit 2BR or Studio+/1BR+ pair
            const canFit2Slot = remainingSlots >= 2; // Can fit 1BR
            
            if (canFit3Slot || canFit2Slot) {
              // NEVER place studios if we can fit larger units - prioritize revenue
              unitsToAllocate = 0;
            } else {
              // Only place studios if we have single isolated slots
              unitsToAllocate = maxUnits;
            }
          }
          
          if (unitsToAllocate > 0) {
            if (unitType.type === 'paired') {
              // For paired units, allocate both Studio+ and 1BR+
              unitMix.studioPlus = unitsToAllocate;
              unitMix.oneBRPlus = unitsToAllocate;
            } else {
              // For standalone units
              unitMix[unitType.key] = unitsToAllocate;
            }
            
            remainingSlots -= unitsToAllocate * unitType.slots;
            
            console.log(`📊 Allocated ${unitsToAllocate} ${unitType.key} units (${unitsToAllocate * unitType.slots} slots), ${remainingSlots} slots remaining`);
          }
        }
      }
      
      // ULTRA-AGGRESSIVE FINAL PASS: Maximize revenue by eliminating studios
      while (remainingSlots > 0) {
        let placed = false;
        
        if (remainingSlots >= 3) {
          // Prefer 2BR over tetris pairs for pure revenue
          const twoBRRevenue = calculateRevenue({ twoBR: 1 }, defaultBusinessMix) / 3;
          const pairRevenue = calculateRevenue({ studioPlus: 1, oneBRPlus: 1 }, defaultBusinessMix) / 3;
          
          if (twoBRRevenue >= pairRevenue) {
            unitMix.twoBR = (unitMix.twoBR || 0) + 1;
            remainingSlots -= 3;
            console.log(`📊 Final pass: Added 1 more 2BR (${remainingSlots} slots remaining)`);
            placed = true;
          } else {
            unitMix.studioPlus = (unitMix.studioPlus || 0) + 1;
            unitMix.oneBRPlus = (unitMix.oneBRPlus || 0) + 1;
            remainingSlots -= 3;
            console.log(`📊 Final pass: Added 1 more Studio+/1BR+ pair (${remainingSlots} slots remaining)`);
            placed = true;
          }
        } else if (remainingSlots >= 2) {
          // Always prefer 1BR over studios for revenue
          unitMix.oneBR = (unitMix.oneBR || 0) + 1;
          remainingSlots -= 2;
          console.log(`📊 Final pass: Added 1 more 1BR (${remainingSlots} slots remaining)`);
          placed = true;
        } else if (remainingSlots === 1) {
          // ONLY place studio as absolute last resort
          unitMix.studio = (unitMix.studio || 0) + 1;
          remainingSlots -= 1;
          console.log(`📊 Final pass: Added 1 Studio as last resort (${remainingSlots} slots remaining)`);
          placed = true;
        }
        
        if (!placed) break; // Safety break
      }
      
      // REVENUE OPTIMIZATION: Try to convert adjacent studios to 1BR for better revenue
      if (unitMix.studio >= 2) {
        const studioPairsToConvert = Math.floor(unitMix.studio / 2);
        if (studioPairsToConvert > 0) {
          unitMix.studio -= studioPairsToConvert * 2;
          unitMix.oneBR = (unitMix.oneBR || 0) + studioPairsToConvert;
          console.log(`📊 Revenue optimization: Converted ${studioPairsToConvert * 2} studios to ${studioPairsToConvert} 1BRs`);
        }
      }
      
    } else if (strategy === 'density') {
      // Density strategy: Maximize unit count with studios
      
      // Fill most slots with studios (1 slot each)
      unitMix.studio = Math.floor(remainingSlots * 0.6);
      remainingSlots -= unitMix.studio;
      
      // Add some 1BR units
      const oneBRCount = Math.floor(remainingSlots * 0.6 / 2);
      unitMix.oneBR = Math.min(oneBRCount, Math.floor(remainingSlots / 2));
      remainingSlots -= unitMix.oneBR * 2;
      
      // Add a few 2BR if space allows
      const twoBRCount = Math.floor(remainingSlots / 3);
      unitMix.twoBR = Math.min(twoBRCount, 2);
      remainingSlots -= unitMix.twoBR * 3;
      
      // Add Studio+ & 1BR+ pairs if remaining slots allow
      const pairsCount = Math.floor(remainingSlots / 3);
      const pairsToAdd = Math.min(pairsCount, 1); // Limit to 1 pair for density strategy
      unitMix.studioPlus = pairsToAdd;
      unitMix.oneBRPlus = pairsToAdd;
      remainingSlots -= pairsToAdd * 3;
      
    } else if (strategy === 'premium') {
      // Premium strategy: Focus on larger units, NO STUDIOS if possible
      
      // Start with 2BR units (premium positioning) - be more aggressive
      const twoBRCount = Math.floor(remainingSlots * 0.6 / 3);
      unitMix.twoBR = Math.min(twoBRCount, Math.floor(remainingSlots / 3));
      remainingSlots -= unitMix.twoBR * 3;
      
      // Add Studio+ & 1BR+ pairs (premium tetris units)
      const studioPlusePairs = Math.floor(remainingSlots * 0.8 / 3); // Use most remaining slots
      const pairsToAdd = Math.min(studioPlusePairs, Math.floor(remainingSlots / 3));
      unitMix.studioPlus = pairsToAdd;
      unitMix.oneBRPlus = pairsToAdd;
      remainingSlots -= pairsToAdd * 3;
      
      // Add 1BR units for remaining slots
      const oneBRCount = Math.floor(remainingSlots / 2);
      unitMix.oneBR = oneBRCount;
      remainingSlots -= unitMix.oneBR * 2;
      
      // ONLY add studios if absolutely necessary (1 slot left)
      if (remainingSlots === 1) {
        unitMix.studio = 1;
        remainingSlots -= 1;
      }
      
      // If we still have 2+ slots, try to optimize further
      while (remainingSlots >= 2) {
        if (remainingSlots >= 3) {
          // Add another 2BR if possible
          unitMix.twoBR = (unitMix.twoBR || 0) + 1;
          remainingSlots -= 3;
        } else if (remainingSlots >= 2) {
          // Add another 1BR
          unitMix.oneBR = (unitMix.oneBR || 0) + 1;
          remainingSlots -= 2;
        }
      }
    }

    return unitMix;
  }, [calculateRevenue]);

  // AI-generated recommendations using real market data and slot constraints
  const aiRecommendations = useMemo(() => {
    if (availableSlots === 0) return [];

    // Default business model mix (can be made dynamic later)
    const defaultBusinessMix = {
      marketRate: 60,
      shortTermRental: 30, 
      workforceHousing: 10
    };

    const revenueUnitMix = generateOptimalUnitMix('revenue', availableSlots);
    const densityUnitMix = generateOptimalUnitMix('density', availableSlots);
    const premiumUnitMix = generateOptimalUnitMix('premium', availableSlots);

    return [
      {
        id: 1,
        title: "Maximum Revenue Strategy",
        description: "Optimized for highest rental income in " + currentMarket.city,
        reasoning: `Advanced revenue optimization using real rental rates and unit positioning analysis. Prioritizes 2BR and 1BR units which generate $400+ and $300+ per slot respectively vs $200 for studios. Avoids placing studios in premium end positions (more windows = higher rent). Combines adjacent studios into 1BR units when possible for 50% revenue increase. Uses aggressive 80% allocation for 2BR and 90% for 1BR to maximize income in ${currentMarket.city}. ${currentMarket.marketAnalysis}. Uses ${calculateSlotsUsed(revenueUnitMix)} of ${availableSlots} available slots with ${Math.round((calculateSlotsUsed(revenueUnitMix) / availableSlots) * 100)}% efficiency.`,
        unitMix: revenueUnitMix,
        projectedRevenue: "$" + (calculateRevenue(revenueUnitMix, defaultBusinessMix)).toLocaleString() + "/month",
        marketAnalysis: currentMarket.marketAnalysis,
        averageRent: "$" + Math.round(getMarketAdjustedRates(selectedMarket, 'marketRate').rates.oneBR).toLocaleString() + "/month (1BR)",
        vacancyRate: "Local market conditions favorable",
        totalUnits: Object.values(revenueUnitMix).reduce((sum, count) => sum + count, 0),
        slotsUsed: calculateSlotsUsed(revenueUnitMix)
      },
      {
        id: 2, 
        title: "Maximum Density Strategy",
        description: "Fit the most units while maintaining market viability",
        reasoning: `Emphasize studio units to maximize unit count in ${currentMarket.city} market. Good for workforce housing demand. Uses ${calculateSlotsUsed(densityUnitMix)} of ${availableSlots} available slots.`,
        unitMix: densityUnitMix,
        projectedRevenue: "$" + (calculateRevenue(densityUnitMix, defaultBusinessMix)).toLocaleString() + "/month",
        marketAnalysis: currentMarket.marketAnalysis,
        averageRent: "$" + Math.round(getMarketAdjustedRates(selectedMarket, 'marketRate').rates.studio).toLocaleString() + "/month (Studio)",
        vacancyRate: "High absorption expected",
        totalUnits: Object.values(densityUnitMix).reduce((sum, count) => sum + count, 0),
        slotsUsed: calculateSlotsUsed(densityUnitMix)
      },
      {
        id: 3,
        title: "Premium Focus Strategy", 
        description: "Target higher-end market segment in " + currentMarket.city,
        reasoning: `Concentrate on larger premium units. Prioritizes 2BR units and Studio+/1BR+ tetris pairs which attract higher-income tenants in ${currentMarket.city}. Strong for short-term rental potential. Uses ${calculateSlotsUsed(premiumUnitMix)} of ${availableSlots} available slots.`,
        unitMix: premiumUnitMix,
        projectedRevenue: "$" + (calculateRevenue(premiumUnitMix, defaultBusinessMix)).toLocaleString() + "/month",
        marketAnalysis: currentMarket.marketAnalysis,
        averageRent: "$" + Math.round(getMarketAdjustedRates(selectedMarket, 'marketRate').rates.twoBR).toLocaleString() + "/month (2BR)",
        vacancyRate: "Premium market segment",
        totalUnits: Object.values(premiumUnitMix).reduce((sum, count) => sum + count, 0),
        slotsUsed: calculateSlotsUsed(premiumUnitMix)
      }
    ];
  }, [availableSlots, selectedMarket, currentMarket, calculateRevenue, generateOptimalUnitMix]);

  // Simulate AI analysis
  const handleAnalyze = () => {
    setIsAnalyzing(true);
    setTimeout(() => {
      setIsAnalyzing(false);
    }, 2000);
  };

  const applyRecommendation = (recommendation) => {
    if (onApplyRecommendation) {
      onApplyRecommendation(recommendation.unitMix);
    }
  };

  if (!currentMarket) {
    return (
      <div style={{
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        padding: '16px',
        marginBottom: '12px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#6b7280',
          fontSize: '0.875rem'
        }}>
          <span>🤖</span>
          <span>Select a market to unlock AI unit mix analysis</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#f0f9ff',
      borderRadius: '8px',
      border: '1px solid #bfdbfe',
      padding: '16px'
    }}>
      {/* Header with Run ModIQ Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
      }}>
        <h5 style={{
          fontSize: '0.9rem',
          fontWeight: '600',
          color: '#1e40af',
          margin: 0
        }}>
          Intelligent Unit Mix Advisor
        </h5>
        <button
          onClick={handleAnalyze}
          disabled={isAnalyzing}
          style={{
            padding: '6px 12px',
            backgroundColor: '#EE2A7B',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: '600',
            cursor: isAnalyzing ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            whiteSpace: 'nowrap',
            opacity: isAnalyzing ? 0.8 : 1
          }}
        >
          {isAnalyzing ? (
            <>
              <span style={{ 
                fontSize: '14px',
                animation: 'modiq-spin 0.8s linear infinite',
                display: 'inline-block',
                fontWeight: 'bold'
              }}>
                ◐
              </span>
              Analyzing...
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
              Run ModIQ
            </>
          )}
        </button>
      </div>

      {/* Loading State */}
      {isAnalyzing && (
        <div style={{
          textAlign: 'center',
          padding: '20px',
          color: '#6b7280',
          fontSize: '0.75rem'
        }}>
          <div style={{ marginBottom: '8px' }}>🧠 Analyzing {currentMarket.city} market data...</div>
          <div style={{ marginBottom: '8px' }}>🚨 Calculating egress requirements ({egressSlots.length} egress points)...</div>
          <div>🎯 Optimizing for {availableSlots} usable slots...</div>
        </div>
      )}

      {/* AI Recommendations */}
      {aiRecommendations.length > 0 && !isAnalyzing && (
        <div>
          {/* Best Recommendation */}
          <div style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '12px'
          }}>
            {/* Collapsible Header */}
            <div 
              onClick={() => setShowMaxRevenue(!showMaxRevenue)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: showMaxRevenue ? '8px' : '0',
                cursor: 'pointer'
              }}
            >
              <div>
                <div style={{
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  color: '#92400e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {aiRecommendations[0].title}
                </div>
                <div style={{
                  fontSize: '0.7rem',
                  color: '#6b7280',
                  marginTop: '2px'
                }}>
                  {aiRecommendations[0].description}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    applyRecommendation(aiRecommendations[0]);
                  }}
                  style={{
                    padding: '4px 8px',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Apply
                </button>
                <span style={{ 
                  color: '#92400e', 
                  fontSize: '0.8rem',
                  transform: showMaxRevenue ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.2s ease'
                }}>▼</span>
              </div>
            </div>
            
            {showMaxRevenue && (
              <>
            
            {/* Unit Mix Preview - Detailed Table */}
            {(() => {
              const unitMix = aiRecommendations[0].unitMix;
              const unitLabels = {
                studio: 'Studio',
                oneBR: '1 BR',
                twoBR: '2 BR',
                studioPlus: 'Studio+',
                oneBRPlus: '1BR+'
              };
              
              // Business model mix used for revenue calculation (must match calculateRevenue)
              const businessMix = { marketRate: 60, shortTermRental: 30, workforceHousing: 10 };
              
              // Calculate weighted average rent for each unit type (same as revenue calculation)
              // Uses custom rates if available
              const getWeightedRent = (unitType) => {
                // Use custom rent overrides if available
                if (customRentOverrides && customRentOverrides[unitType]) {
                  return customRentOverrides[unitType];
                }
                
                let weightedRent = 0;
                Object.keys(businessMix).forEach(businessModel => {
                  const percentage = businessMix[businessModel] || 0;
                  const marketData = getMarketAdjustedRates(selectedMarket, businessModel);
                  if (marketData && marketData.rates && marketData.rates[unitType]) {
                    weightedRent += (marketData.rates[unitType] * percentage / 100);
                  }
                });
                return Math.round(weightedRent);
              };
              
              // Build unit details array with weighted rents
              const unitDetails = ['studio', 'oneBR', 'twoBR', 'studioPlus', 'oneBRPlus'].map(type => {
                const count = unitMix[type] || 0;
                const rent = getWeightedRent(type);
                const slots = count * UNIT_SLOT_REQUIREMENTS[type];
                const subtotal = count * rent;
                return { type, label: unitLabels[type], count, rent, slots, subtotal };
              });
              
              return (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  padding: '8px',
                  marginBottom: '8px',
                  fontSize: '0.65rem'
                }}>
                  {/* Table header */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 50px 70px 50px 70px',
                    gap: '4px',
                    paddingBottom: '4px',
                    borderBottom: '1px solid #e5e7eb',
                    marginBottom: '4px',
                    color: '#6b7280',
                    fontWeight: '600'
                  }}>
                    <div>Type</div>
                    <div style={{ textAlign: 'right' }}>Units</div>
                    <div style={{ textAlign: 'right' }}>Rent/mo</div>
                    <div style={{ textAlign: 'right' }}>Slots</div>
                    <div style={{ textAlign: 'right' }}>Subtotal</div>
                  </div>
                  {/* Table rows */}
                  {unitDetails.map(detail => (
                    <div 
                      key={detail.type} 
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 50px 70px 50px 70px',
                        gap: '4px',
                        padding: '2px 0',
                        color: detail.count > 0 ? '#1f2937' : '#9ca3af'
                      }}
                    >
                      <div style={{ fontWeight: detail.count > 0 ? '600' : '400' }}>{detail.label}</div>
                      <div style={{ textAlign: 'right' }}>{detail.count}</div>
                      <div style={{ textAlign: 'right' }}>${detail.rent.toLocaleString()}</div>
                      <div style={{ textAlign: 'right' }}>{detail.slots}</div>
                      <div style={{ textAlign: 'right', fontWeight: detail.count > 0 ? '600' : '400' }}>
                        ${detail.subtotal.toLocaleString()}
                      </div>
                    </div>
                  ))}
                  {/* Egress row */}
                  <div 
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 50px 70px 50px 70px',
                      gap: '4px',
                      padding: '4px 0 2px 0',
                      marginTop: '4px',
                      borderTop: '1px dashed #d1d5db',
                      color: '#dc2626'
                    }}
                  >
                    <div style={{ fontWeight: '500' }}>Egress</div>
                    <div style={{ textAlign: 'right' }}>—</div>
                    <div style={{ textAlign: 'right' }}>—</div>
                    <div style={{ textAlign: 'right' }}>{egressSlotsTotal}</div>
                    <div style={{ textAlign: 'right' }}>—</div>
                  </div>
                  {/* Total row */}
                  <div 
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 50px 70px 50px 70px',
                      gap: '4px',
                      padding: '4px 0 0 0',
                      marginTop: '4px',
                      borderTop: '1px solid #374151',
                      color: '#1f2937',
                      fontWeight: '700'
                    }}
                  >
                    <div>TOTAL</div>
                    <div style={{ textAlign: 'right' }}>{aiRecommendations[0].totalUnits}</div>
                    <div style={{ textAlign: 'right' }}>—</div>
                    <div style={{ textAlign: 'right' }}>{aiRecommendations[0].slotsUsed + egressSlotsTotal}</div>
                    <div style={{ textAlign: 'right' }}>{aiRecommendations[0].projectedRevenue.replace('/month', '')}</div>
                  </div>
                </div>
              );
            })()}
            
            {/* Key Metrics */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '0.7rem',
              color: '#374151',
              marginBottom: '4px'
            }}>
              <span><strong>Total Revenue:</strong> {aiRecommendations[0].projectedRevenue}</span>
              <span><strong>Units:</strong> {aiRecommendations[0].totalUnits}</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '0.7rem',
              color: '#374151'
            }}>
              <span><strong>Building:</strong> {aiRecommendations[0].slotsUsed + egressSlotsTotal} slots <span style={{ color: '#dc2626' }}>({egressSlotsTotal} egress)</span></span>
              <span><strong>Efficiency:</strong> {Math.round((aiRecommendations[0].slotsUsed / availableSlots) * 100)}%</span>
            </div>
            </>
            )}
          </div>

          {/* Alternative Scenarios */}
          <div style={{ marginBottom: '12px' }}>
            <h6 style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: '#374151',
              margin: '0 0 8px 0'
            }}>
              Alternative Scenarios:
            </h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {aiRecommendations.slice(1).map((recommendation, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '4px',
                  fontSize: '0.7rem'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#374151' }}>
                      {recommendation.title}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.65rem' }}>
                      {recommendation.totalUnits} units, {recommendation.slotsUsed}/{availableSlots} slots ({Math.round((recommendation.slotsUsed / availableSlots) * 100)}%)
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.65rem' }}>
                      {recommendation.projectedRevenue}
                    </div>
                  </div>
                  <button
                    onClick={() => applyRecommendation(recommendation)}
                    style={{
                      padding: '2px 6px',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      fontSize: '0.65rem',
                      cursor: 'pointer'
                    }}
                  >
                    Apply
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* AI Reasoning Details - Collapsible */}
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #bfdbfe',
            borderRadius: '6px',
            padding: '8px',
            marginBottom: '12px'
          }}>
            <h6 
              onClick={() => setShowAIReasoning(!showAIReasoning)}
              style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#1e40af',
                margin: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <span>🧠 AI Reasoning</span>
              <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                {showAIReasoning ? '▼' : '▶'}
              </span>
            </h6>
            {showAIReasoning && (
              <div style={{
                fontSize: '0.7rem',
                color: '#374151',
                lineHeight: '1.4',
                marginTop: '8px'
              }}>
                {aiRecommendations[0].reasoning}
              </div>
            )}
          </div>

          {/* Market Analysis - Collapsible */}
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: '6px',
            padding: '8px'
          }}>
            <h6 
              onClick={() => setShowMarketAnalysis(!showMarketAnalysis)}
              style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#15803d',
                margin: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <span>📊 Market Analysis</span>
              <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                {showMarketAnalysis ? '▼' : '▶'}
              </span>
            </h6>
            {showMarketAnalysis && (
              <div style={{
                fontSize: '0.7rem',
                color: '#374151',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                marginTop: '8px'
              }}>
                <div><strong>Market:</strong> {aiRecommendations[0].marketAnalysis}</div>
                <div><strong>Sample Rate:</strong> {aiRecommendations[0].averageRent}</div>
                <div><strong>Business Mix:</strong> 60% Market Rate, 30% STR, 10% Workforce</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IntelligentUnitMixAdvisor; 